import moment from "moment-timezone";
import Decimal from "decimal.js";
import { useState, useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BirthdayFundRequest, ListRequestsResponse } from "@/lib/api/fund";
import { CommonDestinationTimezone, formatCurrency, cn } from "@/lib/utils";
import { formatNumberToDisplay } from "@/lib/api/common";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { sum, sumDecimal } from "@/components/ui/DataTable/aggregation/index";
import { ResponsivePie } from "@nivo/pie";

// Typed wrappers so aggregationFn satisfies DataTableColumn's AggregationFn<OrgSummaryRow>
const sumAggregation = sum as any;
const sumDecimalAggregation = sumDecimal as any;

type OrgSummaryRow = {
  organization: string;
  nominated_organization: string;
  was_requested: number;
  was_granted: number;
  amount_granted_numeric: number;
};

export function ByOrganizationSummary({ requests }: { requests: BirthdayFundRequest[] }) {
  const columns: DataTableColumn<OrgSummaryRow>[] = [
    {
      id: "organization",
      accessorKey: "organization",
      header: "Organization",
      accessorFn: (row) => row.organization,
      aggregationFn: "firstChild" as any,
      cell: (info) => info.getValue() as string,
    },
    {
      id: "nominated_organization",
      accessorKey: "nominated_organization",
      header: "Nominated Organization",
      accessorFn: (row) => row.nominated_organization,
      aggregationFn: "firstChild" as any,
      cell: (info) => info.getValue() as string,
    },
    {
      id: "total_granted",
      accessorKey: "total_granted",
      header: "Total Granted",
      accessorFn: (row) => row.was_granted,
      aggregationFn: sumAggregation,
      cell: (info) => info.getValue() as number,
    },
    {
      id: "total_requested",
      accessorKey: "total_requested",
      header: "Total Requested",
      accessorFn: (row) => row.was_requested,
      aggregationFn: sumAggregation,
      cell: (info) => info.getValue() as number,
    },
    {
      id: "amount_granted_numeric",
      accessorKey: "amount_granted_numeric",
      header: "Amount Granted",
      accessorFn: (row) => row.amount_granted_numeric,
      aggregationFn: sumDecimalAggregation,
      cell: (info) => {
        const value = info.getValue() as number;
        return value ? formatCurrency(value) : "--";
      },
      aggregatedCell: (info) => {
        const value = info.getValue() as number;
        return value ? formatCurrency(value) : "--";
      },
    },
  ];

  const tableData: OrgSummaryRow[] = requests.map((r) => ({
    organization: r.organization || r.nominated_organization,
    nominated_organization: r.nominated_organization || "Unknown",
    was_requested: 1,
    was_granted: (r.has_been_granted && new Decimal(r.amount_granted).gt(0)) ? 1 : 0,
    amount_granted_numeric: r.amount_granted ? new Decimal(r.amount_granted).toNumber() : 0,
  }));

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4">Funds by Organization</h3>
      <div className="space-y-4">
        <DataTable
          tableClassName="max-h-[680px] overflow-y-scroll"
          columns={columns}
          data={tableData}
          enableSorting
          enableMultiSort
          enableFiltering
          enableColumnVisibility
          enableGrouping
          enableExport
          stickyHeaders
          groupBy={["organization", "nominated_organization"]}
          defaultHideLeafRows={true}
          exportFilename={() => ["organization", moment().format("YYYYMMDD")].join("_")}
        />
      </div>
    </Card>
  );
}

